import { DocumentStatus, DocumentVersion, PolicyDocument } from '@interfaces/documents.interface';
import { buildMockDocument } from '@mocks/documents/document.data';
import { HttpResponse } from 'msw';

// In-memory state so the mocked flow behaves like the real backend:
// update persists the HTML, finalize/revert flip the status.
const documents = new Map<string, PolicyDocument>();

const getOrCreate = (policyId: string): PolicyDocument => {
	const existing = documents.get(policyId);
	if (existing) return existing;
	const created = buildMockDocument(policyId, DocumentStatus.DRAFT);
	documents.set(policyId, created);
	return created;
};

export const getDocument = ({ params }: { params: Record<string, string | readonly string[]> }) => {
	const policyId = String(params.policyId);
	if (policyId === '404-404') return HttpResponse.json({ message: 'Document not found' }, { status: 404 });
	return HttpResponse.json({ data: getOrCreate(policyId) });
};

export const updateDocument = async ({
	params,
	request,
}: {
	params: Record<string, string | readonly string[]>;
	request: Request;
}) => {
	const policyId = String(params.policyId);
	const body = (await request.json()) as { file: string };
	const current = getOrCreate(policyId);
	if (current.status !== DocumentStatus.DRAFT)
		return HttpResponse.json({ message: 'Document is not in draft status' }, { status: 409 });
	const updated = { ...current, file: body.file };
	documents.set(policyId, updated);
	return HttpResponse.json({ data: updated });
};

export const finalizeDocument = ({ params }: { params: Record<string, string | readonly string[]> }) => {
	const policyId = String(params.policyId);
	const current = getOrCreate(policyId);
	if (current.status !== DocumentStatus.DRAFT)
		return HttpResponse.json({ message: 'Document is not in draft status' }, { status: 409 });
	const finalized = { ...current, status: DocumentStatus.FINALIZED };
	documents.set(policyId, finalized);
	return HttpResponse.json({ data: finalized });
};

export const revertDocument = ({ params }: { params: Record<string, string | readonly string[]> }) => {
	const policyId = String(params.policyId);
	const current = getOrCreate(policyId);
	if (current.status !== DocumentStatus.FINALIZED)
		return HttpResponse.json({ message: 'Document is not finalized' }, { status: 409 });
	const reverted = { ...current, status: DocumentStatus.DRAFT };
	documents.set(policyId, reverted);
	return HttpResponse.json({ data: reverted });
};

export const generateDraft = ({ params }: { params: Record<string, string | readonly string[]> }) => {
	const policyId = String(params.policyId);
	const current = getOrCreate(policyId);
	const updated = { ...current, file: `${current.file}<p data-regenerated></p>` };
	documents.set(policyId, updated);
	return HttpResponse.json({ data: updated }, { status: 201 });
};

/** Returns three mocked versions of the policy document, newest first. */
export const getVersions = ({ params }: { params: Record<string, string | readonly string[]> }) => {
	const policyId = String(params.policyId);
	const metadata = { policyId };
	const type = 'documents-health-pyme';
	const versions: DocumentVersion[] = [
		{
			id: 'e5a1c1e0-0000-4000-8000-000000000003',
			type,
			version: 3,
			status: DocumentStatus.DRAFT,
			metadata,
			createdAt: '2026-09-20T10:15:00.000Z',
			updatedAt: '2026-09-21T09:00:00.000Z',
		},
		{
			id: 'e5a1c1e0-0000-4000-8000-000000000002',
			type,
			version: 2,
			status: DocumentStatus.DEPRECATED,
			metadata,
			createdAt: '2026-09-10T12:00:00.000Z',
			updatedAt: '2026-09-20T10:15:00.000Z',
			rejectionHistory: [
				{ description: 'Error en la razón social del contratante', revertedAt: '2026-09-20T10:15:00.000Z' },
			],
		},
		{
			id: 'e5a1c1e0-0000-4000-8000-000000000001',
			type,
			version: 1,
			status: DocumentStatus.FINALIZED,
			metadata,
			createdAt: '2026-09-01T08:30:00.000Z',
			updatedAt: '2026-09-10T12:00:00.000Z',
		},
	];
	return HttpResponse.json({ data: versions });
};

export const getPreviewPdf = () => {
	// Minimal PDF header: enough for the browser to treat it as a PDF stream.
	const pdfBytes = new TextEncoder().encode('%PDF-1.4\n%%EOF');
	return new HttpResponse(pdfBytes, { headers: { 'Content-Type': 'application/pdf' } });
};
